import { useEffect } from 'react'

interface MetaOptions {
  title: string
  description: string
  socialTitle?: string
  socialDescription?: string
  socialImage?: string
  canonical?: string
}

function upsertMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }
  element.setAttribute('href', href)
}

/**
 * Public metadata only — no license number, residential address or other
 * private information is written to the document head.
 */
export function useDocumentMeta({
  title,
  description,
  socialTitle,
  socialDescription,
  socialImage,
  canonical,
}: MetaOptions): void {
  useEffect(() => {
    document.title = title
    upsertMeta('meta[name="description"]', 'name', 'description', description)
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', socialTitle ?? title)
    upsertMeta(
      'meta[property="og:description"]',
      'property',
      'og:description',
      socialDescription ?? description,
    )
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'website')
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')
    if (socialImage) {
      upsertMeta('meta[property="og:image"]', 'property', 'og:image', socialImage)
      upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', socialImage)
    }
    if (canonical) {
      upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical)
      upsertLink('canonical', canonical)
    }
  }, [title, description, socialTitle, socialDescription, socialImage, canonical])
}

/** Injects a JSON-LD block containing approved public information only. */
export function useStructuredData(id: string, data: Record<string, unknown>): void {
  useEffect(() => {
    let script = document.getElementById(id) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = id
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(data)
    return () => {
      script?.remove()
    }
  }, [id, data])
}
